import React, { FC, useCallback, useEffect, useState } from "react";
import { Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import DropZone from "../../../components/elements/DropZone";
import SavedZipCodes from "./components/SavedZipCodes";
import classes from "./ZipCodeSelectionSlide.module.scss";
import { PageStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster } from "../../../helpers/toasters";
import { setSelectedZipCodes } from "../../../redux/reducers/campaignToCreate";

const ZipCodeSelectionSlide: FC<PageStepComponentProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}) => {
  const selectedZipCodes = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedZipCodes);
  const [zipCodes, setZipCodes] = useState<string[]>(selectedZipCodes);
  const [didErrorOccurInUpload, setUploadError] = useState<boolean>(false);

  const dispatch = useDispatch();

  const validateCSV = useCallback((csvRowsWithoutHeader: string[]) => {
    const zipCodesArray: string[] = [];

    for (const csvRow of csvRowsWithoutHeader) {
      if (csvRow.length === 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const parts = csvRow.split(",");

      for (const part of parts) {
        const zipCode = part.trim();
        if (zipCode.length === 0) {
          // eslint-disable-next-line no-continue
          continue;
        }

        zipCodesArray.push(zipCode);
      }
    }

    return zipCodesArray;
  }, []);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setUploadError(false);
      acceptedFiles.forEach(async (file: any) => {
        try {
          const reader = new FileReader();
          reader.readAsText(file);
          reader.addEventListener("load", async (event) => {
            const csv: string = event.target!.result as string;
            // split the csv by new line
            const csvRows = csv.split("\n");

            const validatedZipCodes = validateCSV(csvRows);
            const uniqueZipCodes = [...new Set(validatedZipCodes)];
            setZipCodes(uniqueZipCodes);
          });
        } catch (error) {
          console.log("There was an error!:", error);
          setUploadError(true);
        }
      });
    },
    [validateCSV],
  );

  useEffect(() => {
    if (isAttemptingToGoNext && setIsAttemptingToGoNext && handleNext) {
      if (zipCodes.length !== 0) {
        dispatch(setSelectedZipCodes(zipCodes));
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, enter the required zip codes.", 5000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [dispatch, handleNext, isAttemptingToGoNext, setIsAttemptingToGoNext, zipCodes, zipCodes.length]);

  const mainContent = () => (
    <div className={classes.container}>
      <div className={classes.dropZoneContainer}>
        <DropZone
          explanatoryText="Please upload an CSV file where each zip code should be on a new line."
          sampleLink="https://www.brandgen.io/hubfs/Help%20Documents/BG_Sample_ZipCodes.csv"
          onDrop={onDrop}
          didErrorOccurInUpload={didErrorOccurInUpload}
        />
      </div>

      {zipCodes.length !== 0 ? <SavedZipCodes zipCodes={zipCodes} /> : null}
    </div>
  );

  return (
    <div
      style={{
        padding: !setIsAttemptingToGoNext ? "5rem 3rem" : 0,
      }}
    >
      {setIsAttemptingToGoNext ? (
        <CampaignCreationSlideContainer heading="Campaign Zip Code Selection">
          {mainContent()}
        </CampaignCreationSlideContainer>
      ) : (
        <>
          <Typography variant="h2" className={!setIsAttemptingToGoNext ? classes.h2Title : ""}>
            Upload Zip Codes via CSV
          </Typography>
          {mainContent()}
        </>
      )}
    </div>
  );
};

export default ZipCodeSelectionSlide;
